import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { LogoPlanet } from "./LogoPlanet";
import { isLowPower } from "../utils/perf";

// tiny starfield for inside the system
const SYS_STARS = Array.from({ length: 16 }, (_, i) => {
  const r = (n) => {
    const x = Math.sin(i * 91.7 + n * 151.3) * 43758.5453;
    return x - Math.floor(x);
  };
  return { x: `${(r(1) * 100).toFixed(1)}%`, y: `${(r(2) * 100).toFixed(1)}%`, s: 1.5 + r(3) * 1.8, d: `${(r(4) * 5).toFixed(1)}s` };
});

// 3D orbital "solar system" the visitor scrolls through: a glowing central
// body with product cutouts gliding along tilted elliptical rings. Rings spin
// continuously (CSS), the whole system rotates/tilts with scroll (framer).
// bodies: [{ src, label, ring (0-2), phase (deg), dur (s), size (px), glow }]
const RING_R = [190, 300, 410];

const OrbitBody = ({ body, reduce }) => {
  const r = RING_R[body.ring];
  const spin = reduce
    ? {}
    : { animationDuration: `${body.dur}s`, animationDelay: `${-((body.phase || 0) / 360) * body.dur}s` };
  const counter = reduce
    ? {}
    : { animationDuration: `${body.dur}s`, animationDelay: `${-((body.phase || 0) / 360) * body.dur}s` };
  return (
    <div
      className={"orb-body orb-spin absolute left-1/2 top-1/2"}
      style={{ ...spin, transformStyle: "preserve-3d", willChange: "transform" }}
      aria-hidden={body.decorative ? "true" : undefined}
    >
      <div style={{ transform: `translate(-50%,-50%) translateY(${-r}px)`, transformStyle: "preserve-3d" }}>
        <div className="orb-spin-rev" style={{ ...counter, transformStyle: "preserve-3d" }}>
          <div className="flex flex-col items-center gap-2" style={{ transform: "rotateX(-56deg)" }}>
            {/* cheap glow disc behind the cutout — a CSS filter on an
                animating 3D layer forces repaints every frame (the lag) */}
            <div className="relative" style={{ willChange: "transform" }}>
              <span
                aria-hidden="true"
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ width: body.size * 1.3, height: body.size * 1.3, background: `radial-gradient(circle, ${body.glow || "rgba(233,180,76,0.4)"} 0%, transparent 70%)` }}
              />
              <img
                src={body.src}
                alt={body.label || ""}
                loading="lazy"
                decoding="async"
                draggable="false"
                className="relative w-auto object-contain"
                style={{ height: body.size }}
              />
            </div>
            {body.label && (
              <span className="orb-label whitespace-nowrap rounded-full border border-volt/25 bg-espresso/85 px-3 py-1 font-mono text-[9px] uppercase tracking-[0.22em] text-volt/90 shadow-[0_0_16px_rgba(233,180,76,0.25)]">
                {body.label}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const SolarSystem = ({ center, bodies, testid = "solar-system", className = "" }) => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress: p } = useScroll({ target: ref, offset: ["start end", "end start"] });
  // Scroll scrubs the whole system's rotation + tilt + depth.
  const rotateZ = useTransform(p, [0, 1], [-24, 24]);
  const rotateX = useTransform(p, [0, 1], [50, 62]);
  const scale = useTransform(p, [0, 0.5, 1], [0.92, 1, 0.94]);

  return (
    <div
      ref={ref}
      data-testid={testid}
      className={`solar-sys relative flex h-[70vh] min-h-[480px] items-center justify-center overflow-hidden sm:h-[80vh] ${className}`}
      style={{ perspective: "1300px" }}
    >
      {/* central glow */}
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-volt/10 blur-3xl" aria-hidden="true" />

      {/* tilted orbital plane */}
      <div className="absolute left-1/2 top-1/2 scale-[0.52] sm:scale-90 lg:scale-100" style={{ transformStyle: "preserve-3d" }}>
      <motion.div
        aria-hidden="true"
        style={reduce ? { x: "-50%", y: "-50%" } : { x: "-50%", y: "-50%", rotateZ, rotateX, transformStyle: "preserve-3d" }}
      >
        {/* wireframe globe meridians + latitudes around the center */}
        <div aria-hidden="true" className="globe-spin absolute left-1/2 top-1/2" style={{ transformStyle: "preserve-3d" }}>
          {[0, 45, 90, 135].map((deg) => (
            <span
              key={deg}
              className="wire-ring"
              style={{ width: 340, height: 340, transform: `translate(-50%,-50%) rotateY(${deg}deg)` }}
            />
          ))}
          {[-0.55, 0, 0.55].map((t, i) => (
            <span
              key={i}
              className="wire-ring"
              style={{
                width: 340 * Math.cos(t),
                height: 340 * Math.cos(t),
                transform: `translate(-50%,-50%) rotateX(90deg) translateZ(${Math.sin(t) * 170}px)`,
              }}
            />
          ))}
        </div>
        {RING_R.map((r, i) => (
          <div
            key={r}
            className="absolute left-1/2 top-1/2 rounded-full border border-ink/12"
            style={{ width: r * 2, height: r * 2, transform: "translate(-50%,-50%)" }}
          />
        ))}
        {bodies.map((b, i) => (
          <OrbitBody key={`${b.src}-${i}`} body={b} reduce={reduce} />
        ))}
      </motion.div>
      </div>

      {/* real starfield photo layer behind the orbits */}
      <img
        src="/planets/starfield.jpg"
        alt=""
        aria-hidden="true"
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover opacity-45"
      />

      {/* stars inside the system */}
      <div aria-hidden="true" className="absolute inset-0">
        {SYS_STARS.map((s, i) => (
          <span key={i} className="star-dot" style={{ left: s.x, top: s.y, width: s.s, height: s.s, animationDelay: s.d }} />
        ))}
      </div>

      {/* central body — stays upright above the plane */}
      <motion.div
        className="relative z-10 flex flex-col items-center gap-3"
        style={reduce ? undefined : { scale }}
        animate={reduce ? {} : { y: [0, -12, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      >
        {center.logoPlanet ? (
          <LogoPlanet label={center.label} />
        ) : center.ring ? (
          <div className="relative">
            <img
              src={center.src}
              alt={center.label || ""}
              draggable="false"
              className="h-32 w-32 rounded-full object-cover sm:h-44 sm:w-44"
              style={{ filter: `drop-shadow(0 0 56px ${center.glow || "rgba(77,164,255,0.6)"})` }}
            />
            {/* blue planetary ring */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-1/2 h-[175%] w-[175%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border-2 border-cyan/50"
              style={{ transform: "translate(-50%,-50%) rotateX(72deg)", boxShadow: "0 0 24px rgba(79,179,191,0.35), inset 0 0 18px rgba(79,179,191,0.25)" }}
            />
            <span
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-1/2 h-[200%] w-[200%] -translate-x-1/2 -translate-y-1/2 rounded-[50%] border border-volt/30"
              style={{ transform: "translate(-50%,-50%) rotateX(72deg)" }}
            />
          </div>
        ) : center.round ? (
          <img
            src={center.src}
            alt={center.label || ""}
            draggable="false"
            className="h-28 w-28 rounded-full object-cover sm:h-40 sm:w-40"
            style={{ filter: "drop-shadow(0 0 52px rgba(120,220,160,0.45)) saturate(1.2)" }}
          />
        ) : (
          <img
            src={center.src}
            alt={center.label || ""}
            draggable="false"
            className="h-44 w-auto sm:h-60"
            style={{ filter: `drop-shadow(0 0 52px ${center.glow || "rgba(46,107,230,0.5)"})` }}
          />
        )}
        {center.label && (
          <span className="rounded-full border border-gold/40 bg-espresso/90 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.26em] text-gold-soft shadow-[0_0_22px_rgba(201,155,63,0.35)]">
            {center.label}
          </span>
        )}
      </motion.div>
    </div>
  );
};
